import type { LineItemPropertySetting } from "./tracking-page-definition";

export interface LineItemProperty {
  name: string;
  value: string;
}

// Shopify bundle apps mark component line items with either spelling.
const BUNDLE_CHILD_PROPERTY_NAMES = ["_builderChild", "_builderchild"];

function propertyValue(
  properties: LineItemProperty[] | undefined,
  name: string,
): string | undefined {
  return properties?.find((property) => property.name === name)?.value;
}

export function filterBundleComponentLineItems<
  T extends { properties?: LineItemProperty[] },
>(lineItems: T[], hideBundleComponents: boolean | undefined): T[] {
  if (!hideBundleComponents) {
    return lineItems;
  }
  return lineItems.filter(
    (item) =>
      !BUNDLE_CHILD_PROPERTY_NAMES.some(
        (name) => propertyValue(item.properties, name) === "true",
      ),
  );
}

export function isDisplayableProperty(
  customFields: ReadonlySet<string>,
  property: LineItemProperty,
): boolean {
  return customFields.has(property.name) && property.value.trim() !== "";
}

// Allowlist order; the first non-blank value wins for a repeated name.
export function selectDisplayableProperties(
  properties: LineItemProperty[] | undefined,
  customFields: string[] | undefined,
): LineItemProperty[] {
  if (!customFields?.length) {
    return [];
  }
  return customFields.flatMap((name) => {
    const property = properties?.find(
      (candidate) => candidate.name === name && candidate.value.trim() !== "",
    );
    return property ? [property] : [];
  });
}

export interface LineItemPropertyDisplayRow {
  name: string;
  displayText: string;
}

export function resolveLineItemPropertyDisplayRows({
  properties,
  customFields,
  lineItemPropertySettings,
}: {
  properties: LineItemProperty[] | undefined;
  customFields: string[] | undefined;
  lineItemPropertySettings: LineItemPropertySetting[] | undefined;
}): LineItemPropertyDisplayRow[] {
  return selectDisplayableProperties(properties, customFields).map(
    ({ name, value }) => {
      const setting = lineItemPropertySettings?.find(
        (candidate) => candidate.name === name,
      );
      const formattedValue = value + (setting?.displaySuffix ?? "");
      const displayText =
        setting?.displayLabel === undefined
          ? `${name}: ${formattedValue}`
          : setting.displayLabel === ""
            ? formattedValue
            : `${setting.displayLabel} ${formattedValue}`;
      return { name, displayText };
    },
  );
}
